import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { stockApi, productsApi } from '../api/client';
import Card from '../components/Card';
import Button from '../components/Button';
import QRScanner from '../components/QRScanner';
import useStore from '../store/useStore';
import { parseQrCode } from '../utils/qrParser';

const StockCheck = () => {
  const navigate = useNavigate();
  const { scannedQr, clearScannedQr } = useStore();
  const [showScanner, setShowScanner] = useState(false);
  const [productId, setProductId] = useState(null);

  useEffect(() => {
    if (scannedQr) {
      handleQrScan(scannedQr);
      clearScannedQr();
    }
  }, [scannedQr]);

  const handleQrScan = async (qr) => {
    try {
      // Парсим QR-код для извлечения product_code
      const qrData = parseQrCode(qr);
      const productCode = qrData?.product_code;
      
      if (!productCode) {
        alert('Не удалось извлечь код товара из QR-кода');
        return;
      }
      
      // Ищем товар по product_code
      const response = await productsApi.getByPc(productCode);
      setProductId(response.data.id);
      setShowScanner(false);
    } catch (error) {
      if (error.response?.status === 404) {
        alert('Товар не найден');
      }
    }
  };

  const { data: product } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const response = await productsApi.getById(productId);
      return response.data;
    },
    enabled: !!productId,
  });

  const { data: stock } = useQuery({
    queryKey: ['stock', productId],
    queryFn: async () => {
      const response = await stockApi.getStock(productId);
      return response.data;
    },
    enabled: !!productId,
  });

  if (showScanner) {
    return (
      <QRScanner
        onScan={handleQrScan}
        onError={(err) => alert(`Ошибка сканирования: ${err}`)}
        onClose={() => setShowScanner(false)}
      />
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Проверка остатков</h1>

      {!productId ? (
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => setShowScanner(true)}
        >
          📷 Сканировать QR-код
        </Button>
      ) : (
        <>
          {product && (
            <Card className="mb-4">
              <h3 className="text-lg font-semibold mb-2">{product.name}</h3>
              <p className="text-sm text-gray-500 mb-2">QR: {product.qr_code}</p>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Текущий остаток</p>
                <p className="text-4xl font-bold text-blue-600">
                  {stock?.stock ?? 0}
                </p>
              </div>
            </Card>
          )}

          <div className="space-y-3">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => {
                setProductId(null);
                setShowScanner(true);
              }}
            >
              Сканировать еще
            </Button>
            {product && (
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => navigate(`/products/${product.id}`)}
              >
                Просмотр товара
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default StockCheck;
